/**
 * Projects and sys-configs API.
 */

import { apiFetch } from './fetch';
import { API } from '../constants';
import type { Project, ProjectStatus } from '../types/dvms.types';
import type { PageParams } from './crud';

// ─── Sys Configs ────────────────────────────────────────────────────────────

export interface SysConfigResponse {
    id: string;
    configType: string;
    label: string;
    value: string;
    colorCode?: string;
    sortOrder: number;
    isActive: boolean;
}

export async function listSysConfigs(type: string): Promise<SysConfigResponse[]> {
    const result = await apiFetch<SysConfigResponse[] | null>(`${API.SYS_CONFIGS}?type=${encodeURIComponent(type)}`);
    return Array.isArray(result) ? result.sort((a, b) => a.sortOrder - b.sortOrder) : [];
}

// ─── Projects ────────────────────────────────────────────────────────────────

interface BackendProjectResponse {
    id: string;
    dvmsId: string;
    upid?: string;
    title: string;
    customerId: string;
    customerName: string;
    status: string;
    statusId?: string;
    type?: string;
    showCode?: string;
    ovLanguageId?: string;
    ovLanguageName?: string;
    profitCentreId?: string;
    profitCentreName?: string;
    /** The profit centre's currency — the project's invoice currency. Detail responses only. */
    profitCentreCurrency?: string;
    runtime?: number | null;
    pmFeeMarkup?: number;
    commentsId?: string;
    episodeCount: number;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
    createdByName?: string;
    modifiedAt?: string;
    modifiedBy?: string;
    modifiedByName?: string;
    /** Update only: per-minute line items re-derived by a runtime / episode-count change (DUBVM-80). */
    recalculatedLineItems?: number;
    /** DVMS ID of the project this one was cloned from (DUBVM-75). Detail/clone responses only. */
    clonedFromDvmsId?: string;
}

/**
 * A project as returned by updateProject: the saved project plus, when a
 * runtime / episode-count change re-derived per-minute quantities server-side
 * (DUBVM-80), how many line items were updated.
 */
export type UpdatedProject = Project & { recalculatedLineItems?: number };

interface PaginatedProjectResponse {
    data: BackendProjectResponse[];
    total?: number;
    skip: number;
    top: number;
}

function toProject(r: BackendProjectResponse): Project {
    return {
        id: r.id,
        dvmsProjectId: r.dvmsId,
        universalProjectId: r.upid ?? '',
        customerId: r.customerId,
        customerName: r.customerName,
        title: r.title,
        showCode: r.showCode ?? '',
        ovLanguage: r.ovLanguageName ?? '',
        ovLanguageId: r.ovLanguageId ?? '',
        type: r.type ?? '',
        runtime: r.runtime ?? null,
        pmFeeMarkup: r.pmFeeMarkup ?? 15,
        seriesNumber: '',
        profitCentreId: r.profitCentreId ?? '',
        profitCentreName: r.profitCentreName ?? '',
        currency: r.profitCentreCurrency ?? 'USD',
        episodeCount: r.episodeCount,
        firstDue: '',
        lastDue: '',
        status: r.status as ProjectStatus,
        statusId: r.statusId ?? '',
        originalClientEstimateTotal: 0,
        originalVendorBidsTotal: 0,
        totalOverages: 0,
        totalVendorCosts: 0,
        totalClientCosts: 0,
        totalPOsIssuedToVendors: 0,
        totalInvoicedToClient: 0,
        createdAt: r.createdAt,
        createdByName: r.createdByName ?? '',
        // exactOptionalPropertyTypes: only set when the backend sent it.
        ...(r.clonedFromDvmsId ? { clonedFromDvmsId: r.clonedFromDvmsId } : {}),
    };
}

export async function listProjects(): Promise<Project[]> {
    const result = await apiFetch<PaginatedProjectResponse | BackendProjectResponse[]>(API.PROJECTS);
    if (Array.isArray(result)) {
        return result.map(toProject);
    }
    return result.data.map(toProject);
}

export async function getProject(id: string): Promise<Project> {
    const result = await apiFetch<BackendProjectResponse>(`${API.PROJECTS}/${id}`);
    return toProject(result);
}

// ─── Expanded Project (single call with nested data) ────────────────────────

import type { ServiceGroupResponse, LineItemResponse } from './serviceGroups';
import type { NoteResponse, AttachmentResponse } from './notes';
import type { Quote } from '../types/dvms.types';

export interface ExpandedServiceGroup extends ServiceGroupResponse {
    lineItems: LineItemResponse[];
}

export interface ExpandedComment {
    note: NoteResponse;
    attachments: AttachmentResponse[];
}

export interface ExpandedProject extends Project {
    serviceGroups?: ExpandedServiceGroup[];
    quotes?: Quote[];
    comments?: ExpandedComment[];
}

/**
 * Fetch a project with optional expanded data.
 * expand can be: 'serviceGroups', 'quotes', 'comments' (comma-separated).
 */
export async function getProjectExpanded(
    id: string,
    expand: string = 'serviceGroups,quotes,comments',
): Promise<ExpandedProject> {
    const result = await apiFetch<
        BackendProjectResponse & {
            serviceGroups?: Array<ServiceGroupResponse & { lineItems?: LineItemResponse[] }>;
            quotes?: Quote[];
            comments?: Array<{ note: NoteResponse; attachments: AttachmentResponse[] }>;
        }
    >(`${API.PROJECTS}/${id}?expand=${encodeURIComponent(expand)}`);

    const project = toProject(result) as ExpandedProject;

    if (result.serviceGroups) {
        project.serviceGroups = result.serviceGroups.map((sg) => ({
            ...sg,
            lineItems: sg.lineItems ?? [],
        }));
    }

    if (result.quotes) {
        project.quotes = result.quotes;
    }

    if (result.comments) {
        project.comments = result.comments;
    }

    return project;
}

/** Filter parameters for the project list API. */
export interface ProjectFilterParams {
    status?: string | undefined;
    search?: string | undefined;
    customerId?: string | undefined;
    createdBy?: string | undefined;
    title?: string | undefined;
    types?: string[] | undefined;
    sortBy?: string | undefined;
    sortOrder?: 'asc' | 'desc' | undefined;
}

/** Result of a paginated project list fetch. */
export interface ProjectPageResult {
    data: Project[];
    total: number;
}

/**
 * Fetch paginated projects with optional filters and sorting.
 * Returns both the page data and the total count from the backend.
 */
export async function listProjectsPaged(params: PageParams & ProjectFilterParams): Promise<ProjectPageResult> {
    const skip = params.page * params.limit;
    const top = params.limit;
    const qs = new URLSearchParams();
    qs.set('$top', String(top));
    qs.set('$skip', String(skip));
    qs.set('$count', 'true');
    if (params.status && params.status !== 'all') {
        qs.set('statusId', params.status);
    }
    if (params.search) {
        qs.set('search', params.search);
    }
    if (params.customerId) {
        qs.set('customerId', params.customerId);
    }
    if (params.createdBy) {
        qs.set('createdBy', params.createdBy);
    }
    if (params.title) {
        qs.set('title', params.title);
    }
    if (params.types?.length) {
        qs.set('type', params.types.join(','));
    }
    if (params.sortBy) {
        qs.set('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
        qs.set('sortOrder', params.sortOrder);
    }

    const result = await apiFetch<PaginatedProjectResponse>(`${API.PROJECTS}?${qs.toString()}`);
    return {
        data: result.data.map(toProject),
        total: result.total ?? 0,
    };
}

export interface CreateProjectRequest {
    dvmsId?: string;
    upid?: string;
    title: string;
    customerId: string;
    customerName: string;
    status: string;
    statusId: string;
    type?: string;
    showCode?: string;
    runtime?: number | null;
    pmFeeMarkup?: number;
    ovLanguageId?: string;
    ovLanguageName?: string;
    profitCentreId?: string;
    profitCentreName?: string;
    episodeCount?: number;
}

export async function createProject(payload: CreateProjectRequest): Promise<Project> {
    const resp = await apiFetch<BackendProjectResponse>(API.PROJECTS, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return toProject(resp);
}

/**
 * DUBVM-75: server-side full clone — config + backsheet + FX locks + notes
 * with attachments (quotes and the updates feed are not copied). The clone
 * comes back titled "Copy of <source>" with a fresh DVMS ID and status reset.
 */
export async function cloneProject(id: string): Promise<Project> {
    const resp = await apiFetch<BackendProjectResponse>(`${API.PROJECTS}/${id}/clone`, {
        method: 'POST',
    });
    return toProject(resp);
}

export async function updateProject(id: string, patch: Partial<Project>): Promise<UpdatedProject> {
    // Map frontend field names to backend field names
    const body: Record<string, unknown> = {};
    if (patch.title !== undefined) {
        body.title = patch.title;
    }
    if (patch.customerId !== undefined) {
        body.customerId = patch.customerId;
    }
    if (patch.customerName !== undefined) {
        body.customerName = patch.customerName;
    }
    if (patch.type !== undefined) {
        body.type = patch.type;
    }
    if (patch.showCode !== undefined) {
        body.showCode = patch.showCode;
    }
    if (patch.runtime !== undefined) {
        body.runtime = patch.runtime;
    }
    if (patch.pmFeeMarkup !== undefined) {
        body.pmFeeMarkup = patch.pmFeeMarkup;
    }
    if (patch.ovLanguage !== undefined) {
        body.ovLanguage = patch.ovLanguage;
    }
    if ((patch as { ovLanguageId?: string }).ovLanguageId !== undefined) {
        body.ovLanguageId = (patch as { ovLanguageId?: string }).ovLanguageId;
    }
    if (patch.universalProjectId !== undefined) {
        body.upid = patch.universalProjectId;
    }
    if (patch.profitCentreId !== undefined) {
        body.profitCentreId = patch.profitCentreId;
    }
    if (patch.profitCentreName !== undefined) {
        body.profitCentreName = patch.profitCentreName;
    }
    if (patch.episodeCount !== undefined) {
        body.episodeCount = patch.episodeCount;
    }
    if (patch.status !== undefined) {
        body.status = patch.status;
    }
    if (patch.statusId !== undefined) {
        body.statusId = patch.statusId;
    }

    const resp = await apiFetch<BackendProjectResponse>(`${API.PROJECTS}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    });
    const project = toProject(resp) as UpdatedProject;
    if (resp.recalculatedLineItems) {
        project.recalculatedLineItems = resp.recalculatedLineItems;
    }
    return project;
}
